import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../../Lib/utils.js";
import { Button } from "../../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { getCompanys } from "../../../provider/adm/Grupos/getCompany";
import { Badge } from "../../ui/badge";

const ScrollListCompanies = ({
  companySelectedToFilter,
  setCompanySelectedToFilter,
  setLoading,
  setCompanyID,
  multiSelect = false,
  disabled = false, // Nueva prop
  onCompanyListChange, 
}) => {
  const [open, setOpen] = useState(false);
  const [listaCompanies, setListaCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Función mejorada para comparar nombres ignorando espacios extras
  const compareNames = (name1, name2) => {
    if (!name1 || !name2) return false;
    // Normalizar: trim + convertir espacios múltiples a uno solo + lowercase para comparación
    const normalize = (str) =>
      str.trim().replace(/\s+/g, " ").toLowerCase();
    return normalize(name1) === normalize(name2);
  };

  // Normalizar selectedCompanies para que siempre sea consistente
  const selectedCompanies = multiSelect
    ? Array.isArray(companySelectedToFilter)
      ? companySelectedToFilter
      : companySelectedToFilter
      ? [companySelectedToFilter]
      : []
    : companySelectedToFilter;

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const companies = await getCompanys();
        setListaCompanies(companies || []);
        if (onCompanyListChange) {
          onCompanyListChange(companies || []); // Llamar al callback con la lista completa
        }
      } catch (error) {
        setListaCompanies([]);
        if (onCompanyListChange) {
          onCompanyListChange([]); // Llamar al callback con una lista vacía en caso de error
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [setLoading]);

  // Helper function to handle selection based on mode
  const handleSelect = (currentValue, companyId) => {
    if (multiSelect) {
      const currentSelection = Array.isArray(companySelectedToFilter)
        ? companySelectedToFilter
        : companySelectedToFilter
        ? [companySelectedToFilter]
        : [];

      // Buscar si ya está seleccionado usando comparación mejorada
      const isAlreadySelected = currentSelection.some((selected) =>
        compareNames(selected, currentValue)
      );

      if (isAlreadySelected) {
        // Remove if already selected - usar comparación mejorada
        const newSelection = currentSelection.filter((name) =>
          !compareNames(name, currentValue)
        );
        setCompanySelectedToFilter(newSelection);
      } else {
        // Add to selection
        const newSelection = [...currentSelection, currentValue];
        setCompanySelectedToFilter(newSelection);
      }
    } else {
      // Original single select behavior - usar comparación mejorada
      const newValue = compareNames(currentValue, companySelectedToFilter)
        ? ""
        : currentValue;
      setCompanySelectedToFilter(newValue);
      if (setCompanyID) {
        setCompanyID(newValue ? companyId : null);
      }
      setOpen(false);
    }
  };

  const filteredCompanies = listaCompanies.filter((company) => {
    const searchableFields = [company.name, company.nit, company.id?.toString()];
    return searchableFields.some(
      (field) =>
        field &&
        typeof field === "string" &&
        field.toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  });

  const getDisplayText = () => {
    if (multiSelect) {
      if (Array.isArray(selectedCompanies) && selectedCompanies.length > 0) {
        return `${selectedCompanies.length} compañías seleccionadas`;
      }
      return "Selecciona compañías...";
    } else {
      if (companySelectedToFilter) {
        // Buscar la compañía usando comparación mejorada
        const found = listaCompanies.find((company) =>
          compareNames(company.name, companySelectedToFilter)
        );

        if (found) {
          return found.name;
        } else {
          // Si no se encuentra, mostrar el valor seleccionado directamente
          return companySelectedToFilter;
        }
      }
      return "Selecciona compañía...";
    }
  };
  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Button
          variant="outline"
          role="combobox"
          type="button"
          className={`w-[200px] justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={disabled}
        >
          {disabled ? "Todas las compañías" : getDisplayText()}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <input
            placeholder="Buscar compañía..."
            className="h-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CommandList>
            <CommandEmpty>No se encontraron compañías.</CommandEmpty>
            <CommandGroup>
              {filteredCompanies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={company.name}
                  onSelect={(currentValue) => {
                    handleSelect(currentValue, company.id);
                  }}
                  className="flex items-center"
                >
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(selectedCompanies) &&
                        selectedCompanies.some((selected) =>
                          compareNames(selected, company.name)
                        )
                      }
                      onChange={() => {}}
                      className="h-4 w-4 mr-2"
                    />
                  )}
                  <div className="flex flex-col">
                    <span>{company.name}</span>
                    <span className="text-xs text-gray-500">
                      NIT: {company.nit}
                    </span>
                  </div>
                  {!multiSelect && (
                    <Check
                      className={cn(
                        "ml-auto",
                        compareNames(companySelectedToFilter, company.name)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ScrollListCompanies;